import './css'

import type { LaunchDarklyToolbar } from '@launchdarkly/toolbar'
import { useLaunchDarklyToolbar } from '@launchdarkly/toolbar/react'
import useKeyboardShortcuts, {
  defaultWithInputOnScreen,
} from '@openphone/lib/useKeyboardShortcuts'
import { observer } from 'mobx-react-lite'
import spriteUrl from 'virtual:openphone-icons/sprite'

import LazyConfetti from '@src/component/LazyConfetti'
import config, { isProd } from '@src/config'
import { ServiceContext } from '@src/service/context'
import {
  eventInterceptionPlugin,
  flagOverridePlugin,
} from '@src/service/feature-flags/FlagsService'
import DesignSystemProvider from '@ui/DesignSystemProvider'
import Toaster from '@ui/Toast/Toaster'
import { useWindowSize } from '@ui/hooks/useWindowSize'

import * as styles from './App.css'
import AppMediaViewer from './AppMediaViewer'
import type AppStore from './AppStore'
import WindowsTitleBar from './WindowsTitleBar'
import { BannersProvider } from './banner'
import PreviewEnvironmentBanner from './banner/PreviewEnvironmentBanner/PreviewEnvironmentBanner'
import PricingProvider from './billing/pricing/PricingProvider/PricingProvider'
import { useAppStore } from './context'
import { GlobalErrorBoundary } from './error'
import './global.css'
import './globalStyles.css'
import Prompt from './prompt/Prompt'
import Router from './router/Router'
import useFavicon from './useFavicon'

import useMetaColorScheme from '@odyssey/hooks/useMetaColorScheme/useMetaColorScheme'
import usePreferredThemeKey from '@odyssey/hooks/usePreferredThemeKey/usePreferredThemeKey'

declare global {
  interface Window {
    app: AppStore
    dataLayer: any[]
    LaunchDarklyToolbar?: LaunchDarklyToolbar
  }
}

const App = function () {
  const { width, height } = useWindowSize()
  const store = useAppStore()
  const preferredThemeKey = usePreferredThemeKey(store.themeKey)
  useMetaColorScheme(store.themeKey)
  useFavicon()

  useLaunchDarklyToolbar({
    clientSideId: config.LAUNCH_DARKLY_CLIENT_SIDE_ID,
    flagOverridePlugin,
    eventInterceptionPlugin,
    position: 'bottom-right',
    enabled: !isProd && store.launchDarklyToolbarVisible,
  })

  // Cmd+K only exists inside the logged-in app shell (Main.tsx), so it can't reach
  // onboarding/activation routes. This shortcut lives here, above the router, so devs
  // can reach the LD toolbar from any route, onboarding included.
  useKeyboardShortcuts({
    name: 'app-dev-ld-toolbar',
    node: isProd ? null : document,
    filter: defaultWithInputOnScreen({ allowedShortcutsAlways: ['Meta+Shift+KeyL'] }),
    handler: (shortcut, event) => {
      if (shortcut === 'Meta+Shift+KeyL') {
        event.preventDefault()
        store.toggleLaunchDarklyToolbar()
      }
    },
    deps: [store],
  })

  return (
    <DesignSystemProvider
      themeKey={preferredThemeKey}
      spriteUrl={spriteUrl}
      typographyThemeKey={store.typographyThemeKey}
      themeVariant={store.themeVariant}
    >
      <div className={styles.root}>
        {store.is('windows') && <WindowsTitleBar app={store} />}
        <div className={styles.body}>
          <ServiceContext.Provider value={store.service}>
            <PricingProvider>
              <GlobalErrorBoundary>
                <Prompt />
                <Toaster />
                <BannersProvider>
                  <PreviewEnvironmentBanner />
                </BannersProvider>
                <Router />
                <AppMediaViewer />
                {store.confetti && (
                  <LazyConfetti
                    recycle={false}
                    width={width}
                    height={height}
                    numberOfPieces={500}
                    tweenDuration={7000}
                    onConfettiComplete={() => store.resetConfetti()}
                  />
                )}
              </GlobalErrorBoundary>
            </PricingProvider>
          </ServiceContext.Provider>
        </div>
      </div>
    </DesignSystemProvider>
  )
}

export default observer(App)
